import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/hooks/use-company';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/api/error';
import { PERMISSIONS } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { NotificationService } from '@/services/notification.service';
import type { AppNotification } from '@/types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Bell, BellOff, CalendarDays, Check, CheckCheck, Inbox, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { PushNotificationPrompt } from './PushNotificationPrompt';

const NOTIFICATION_LIMIT = 50;

function formatNotificationDate(value: string) {
  const date = parseISO(value);
  if (!isValid(date)) return '';

  return {
    relative: formatDistanceToNowStrict(date, { addSuffix: true, locale: ptBR }),
    full: format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR }),
  };
}

function hasBookingLink(notification: AppNotification) {
  return typeof notification.metadata?.bookingId === 'string';
}

interface NotificationItemProps {
  notification: AppNotification;
  canMarkRead: boolean;
  isMarkingRead: boolean;
  onMarkRead: (id: string) => void;
  onOpenBooking: (notification: AppNotification) => void;
}

function NotificationItem({
  notification,
  canMarkRead,
  isMarkingRead,
  onMarkRead,
  onOpenBooking,
}: NotificationItemProps) {
  const isUnread = !notification.readAt;
  const date = formatNotificationDate(notification.createdAt);

  return (
    <article
      className={cn(
        'relative border-b px-5 py-4 transition-colors last:border-b-0',
        isUnread ? 'bg-primary/5' : 'bg-background',
      )}
    >
      <div className="flex gap-3">
        <div
          className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
            isUnread ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
          )}
        >
          <Bell className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <h3 className="text-foreground flex-1 text-sm leading-5 font-semibold">
              {notification.renderedSubject || 'Nova notificação'}
            </h3>
            {isUnread && <span className="bg-primary mt-1.5 h-2 w-2 shrink-0 rounded-full" aria-label="Não lida" />}
          </div>
          <p className="text-muted-foreground mt-1 text-sm leading-5">{notification.renderedMessage}</p>
          {date && (
            <time
              className="text-muted-foreground mt-2 block text-xs"
              dateTime={notification.createdAt}
              title={date.full}
            >
              {date.relative}
            </time>
          )}

          {(hasBookingLink(notification) || (isUnread && canMarkRead)) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {hasBookingLink(notification) && (
                <Button variant="outline" size="sm" onClick={() => onOpenBooking(notification)}>
                  <CalendarDays className="h-3.5 w-3.5" />
                  Ver na agenda
                </Button>
              )}
              {isUnread && canMarkRead && (
                <Button variant="ghost" size="sm" disabled={isMarkingRead} onClick={() => onMarkRead(notification.id)}>
                  <Check className="h-3.5 w-3.5" />
                  Marcar como lida
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

interface NotificationCenterProps {
  className?: string;
  variant?: 'default' | 'sidebar';
}

export function NotificationCenter({ className, variant = 'default' }: NotificationCenterProps) {
  const [open, setOpen] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const { can } = usePermissions();
  const canRead = can(PERMISSIONS.NOTIFICATION_READ);
  const canMarkRead = can(PERMISSIONS.NOTIFICATION_UPDATE);
  const companyId = activeCompany?.id;
  const summaryKey = ['notifications', companyId, 'summary'] as const;
  const listKey = ['notifications', companyId, 'list'] as const;

  const summaryQuery = useQuery({
    queryKey: summaryKey,
    queryFn: NotificationService.getSummary,
    enabled: canRead && Boolean(companyId),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const listQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) => NotificationService.list({ limit: NOTIFICATION_LIMIT, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loadedCount = lastPage.meta.page * lastPage.meta.limit;
      return loadedCount < lastPage.meta.total ? lastPage.meta.page + 1 : undefined;
    },
    enabled: canRead && Boolean(companyId) && open,
  });

  const refreshNotifications = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: summaryKey }),
      queryClient.invalidateQueries({ queryKey: listKey }),
    ]);
  };

  const markReadMutation = useMutation({
    mutationFn: NotificationService.markRead,
    onMutate: setMarkingId,
    onSuccess: () => void refreshNotifications(),
    onError: (error) => {
      toast.error('Não foi possível atualizar a notificação', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    },
    onSettled: () => setMarkingId(null),
  });

  const markAllReadMutation = useMutation({
    mutationFn: NotificationService.markAllRead,
    onSuccess: () => void refreshNotifications(),
    onError: (error) => {
      toast.error('Não foi possível marcar todas como lidas', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    },
  });

  if (!canRead || !companyId) return null;

  const firstPage = listQuery.data?.pages[0];
  const unreadCount = summaryQuery.data?.unreadCount ?? firstPage?.meta.unreadCount ?? 0;
  const visibleUnreadCount = unreadCount > 99 ? '99+' : String(unreadCount);
  const notifications = listQuery.data?.pages.flatMap((page) => page.data) ?? [];

  const openBooking = (notification: AppNotification) => {
    if (!notification.readAt && canMarkRead) {
      markReadMutation.mutate(notification.id);
    }
    setOpen(false);
    navigate('/agenda');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'relative shrink-0',
            variant === 'sidebar' && 'text-sidebar-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground',
            className,
          )}
          aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : 'Notificações'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="bg-destructive text-destructive-foreground absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] leading-none font-bold ring-2 ring-current">
              {visibleUnreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="flex h-dvh w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-5 py-5 pr-12 text-left">
          <div className="flex items-start justify-between gap-4">
            <div>
              <SheetTitle>Notificações</SheetTitle>
              <SheetDescription className="mt-1">
                {unreadCount > 0
                  ? `${unreadCount} ${unreadCount === 1 ? 'notificação não lida' : 'notificações não lidas'}`
                  : 'Você está em dia com as novidades.'}
              </SheetDescription>
            </div>
            {canMarkRead && unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                disabled={markAllReadMutation.isPending}
                onClick={() => markAllReadMutation.mutate()}
              >
                <CheckCheck className="h-4 w-4" />
                Ler todas
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite">
          <PushNotificationPrompt className="border-b" />
          {listQuery.isPending ? (
            <div className="space-y-5 p-5" aria-label="Carregando notificações">
              {[1, 2, 3].map((item) => (
                <div key={item} className="flex gap-3">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : listQuery.isError ? (
            <EmptyState
              compact
              icon={BellOff}
              title="Não foi possível carregar"
              description={getApiErrorMessage(listQuery.error, 'Tente novamente em instantes.')}
              actionLabel="Tentar novamente"
              onAction={() => void listQuery.refetch()}
            />
          ) : notifications.length === 0 ? (
            <EmptyState
              compact
              icon={Inbox}
              title="Nenhuma notificação"
              description="As novidades da empresa aparecerão aqui."
            />
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                canMarkRead={canMarkRead}
                isMarkingRead={markingId === notification.id}
                onMarkRead={(id) => markReadMutation.mutate(id)}
                onOpenBooking={openBooking}
              />
            ))
          )}
        </div>

        {listQuery.hasNextPage && (
          <div className="border-t p-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={listQuery.isFetchingNextPage}
              onClick={() => void listQuery.fetchNextPage()}
            >
              {listQuery.isFetchingNextPage ? 'Carregando…' : 'Carregar mais notificações'}
            </Button>
          </div>
        )}
        <div className="border-t p-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            disabled={listQuery.isFetching}
            onClick={() => void refreshNotifications()}
          >
            <RefreshCw className={cn('h-4 w-4', listQuery.isFetching && 'animate-spin')} />
            Atualizar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
